import { hashCode } from "../index";
import { DynamicCssModel } from "../../models/DynamicCssModel";
import { shouldUseModernTheme } from "../../theme/navigationButtonThemeColors";
import type { Theme } from "../../types/Theme";
import { generateGridCss } from "./grid/gridCore";

/**
 * Part of the localStorage cache key for the generated CSS. Bump it whenever the generator emits
 * different CSS for unchanged settings, or browsers keep serving their cached copy of the old CSS.
 */
const DYNAMIC_CSS_GENERATOR_VERSION = 2;

export function getStoredCss(hashKey: string, cssKey: string, newHash: string): string | null {
    const cachedHash = localStorage.getItem(hashKey);
    if (newHash === cachedHash) {
        return localStorage.getItem(cssKey);
    }
    return null;
}

export function storeCss(hashKey: string, cssKey: string, hash: string, css: string): void {
    localStorage.setItem(hashKey, hash);
    localStorage.setItem(cssKey, css);
}

export function generateCSS(theme: object, dynamicCss: DynamicCssModel): string {
    const settingCssHashKey = "settings-css-hash";
    const settingCssKey = "settings-css";
    const settingsStr = JSON.stringify(dynamicCss);
    const hash = hashCode(
        `${settingsStr}:${shouldUseModernTheme(theme as Theme)}:${DYNAMIC_CSS_GENERATOR_VERSION}`,
    ).toString();

    const storedCss = getStoredCss(settingCssHashKey, settingCssKey, hash);
    if (storedCss) return storedCss;

    const cssForComponents = [];

    if (dynamicCss.HasGridChanges) {
        cssForComponents.push(...generateGridCss(dynamicCss, theme));
    }

    // final css
    const css = cssForComponents.join("\n");
    storeCss(settingCssHashKey, settingCssKey, hash, css);
    return css;
}

export function applyCSS(css: string) {
    const dynamicStyleId = "dynamic-style";
    let styleElement = document.getElementById(dynamicStyleId);
    if (!styleElement) {
        styleElement = document.createElement("style");
        styleElement.id = dynamicStyleId;
        document.head.append(styleElement);
    }
    styleElement.innerHTML = css;
}
